import React, { useState, useEffect } from "react";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import "../../../assets/css/colors.css";
import { useLanguage } from "context/LanguageProvider";



const createNodes = () => {
    const pairs = [
        { id: "V1", position: 0 },
        { id: "V2", position: 1 },
        { id: "V3", position: 2 },
        { id: "V4", position: 3 },
        { id: "V5", position: 4 },
        { id: "V6", position: 5 }
    ];

    return pairs;
};

const CORRECT_ORDER = ["V1", "V2", "V3", "V4", "V5", "V6"];
const INITIAL_VIEW_TIME = 7000;
const RETRY_VIEW_TIME = 5000;

const selectedStageStr = localStorage.getItem("selectedStage");
const selectedStage = selectedStageStr ? JSON.parse(selectedStageStr) : null;
const games = selectedStage?.games ?? [];

var nameGameKey = localStorage.getItem("gameClicked");
var selectedGame = null;
for (let i = 0; i < games.length; i++) {
    if (games[i].game_title === nameGameKey) {
        selectedGame = games[i];
        break;
    }
}


const DoublyLinkedListIntro = () => {
    const { darkMode } = useDarkMode();

    const [nodes] = useState(createNodes());
    const [placedNodes, setPlacedNodes] = useState([null, null, null, null, null]);
    const [availableNodes, setAvailableNodes] = useState([...CORRECT_ORDER]);
    const [isRevealed, setIsRevealed] = useState(false);
    const [timeLeft, setTimeLeft] = useState(INITIAL_VIEW_TIME / 1000);
    const [lives, setLives] = useState(3);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [wrongPlacement, setWrongPlacement] = useState(null);
    const [gameStarted, setGameStarted] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showError, setShowError] = useState(false);
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    useEffect(() => {
        let shuffled;
        do {
            shuffled = [...CORRECT_ORDER].sort(() => Math.random() - 0.5);
        } while (shuffled.every((node, index) => node === CORRECT_ORDER[index]));
        setAvailableNodes(shuffled);
    }, []);

    useEffect(() => {
        if (!isRevealed || showGameOver || showGameWon || !gameStarted) return;

        const interval = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    setIsRevealed(false);
                    setHasStarted(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [isRevealed, showGameOver, showGameWon, gameStarted]);

    useEffect(() => {
        if (showError) {
            const timer = setTimeout(() => {
                setShowError(false);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [showError]);

    useEffect(() => {
        if (lives <= 0) {
            setShowGameOver(true);
        }
    }, [lives]);

    // Start Game Handler
    const startGame = () => {
        setGameStarted(true);
        setIsRevealed(true);
    };

    // Handle retry after wrong placement
    const handleRetry = () => {
        setIsRevealed(true);
        setTimeLeft(RETRY_VIEW_TIME / 1000);
        setLives((prev) => prev - 1);
        setErrorMessage(selLanguage ? "Λάθος τοποθέτηση! Προσπάθησε ξανά." : "Wrong placement! Try again.");
        setShowError(true);

        setWrongPlacement(null);
    };

    // Place node in position
    const handlePlaceNode = (nodeId, position) => {
        if (!hasStarted || isRevealed) return;

        // Check if position is already filled
        if (placedNodes[position] !== null) return;

        const newPlaced = [...placedNodes];
        newPlaced[position] = nodeId;
        setPlacedNodes(newPlaced);

        // Remove from available
        setAvailableNodes((prev) => prev.filter((id) => id !== nodeId));

        // Check if correct
        if (CORRECT_ORDER[position] !== nodeId) {
            setWrongPlacement(position);
            setTimeout(() => {
                // Reset the wrong placement
                const resetPlaced = [...newPlaced];
                resetPlaced[position] = null;
                setPlacedNodes(resetPlaced);
                setAvailableNodes((prev) => [...prev, nodeId]);
                handleRetry();
            }, 1000);
        } else {
            // Check if won
            const allPlaced = newPlaced.every((n) => n !== null);
            if (allPlaced) {
                const isCorrect = newPlaced.every((n, i) => n === CORRECT_ORDER[i]);
                setShowGameWon(true);
            }
        }
    };

    // Drag handlers
    const handleDragStart = (e, nodeId) => {
        e.dataTransfer.setData("nodeId", nodeId);
    };

    const handleDragOver = (e) => e.preventDefault();

    const handleDrop = (e, position) => {
        e.preventDefault();
        const nodeId = e.dataTransfer.getData("nodeId");
        if (nodeId) {
            handlePlaceNode(nodeId, position);
        }
    };

    const getNodeData = (id) => nodes.find((n) => n.id === id);

    const resetGame = () => {
        setPlacedNodes([null, null, null, null, null]);
        // NEVER shuffle to correct order
        let shuffled;
        do {
            shuffled = [...CORRECT_ORDER].sort(() => Math.random() - 0.5);
        } while (shuffled.every((node, index) => node === CORRECT_ORDER[index]));
        setAvailableNodes(shuffled);
        setIsRevealed(false);
        setTimeLeft(INITIAL_VIEW_TIME / 1000);
        setLives(3);
        setShowGameOver(false);
        setShowGameWon(false);
        setHasStarted(false);
        setWrongPlacement(null);
        setGameStarted(false);
    };

    return (
        <div className="main-content" style={{
            minHeight: "100vh",
            background: darkMode
                ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724 100%)"
                : "var(--lightModeGameBackground)",
            padding: "30px 10px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",

        }}>
            <GameOverModal isVisible={showGameOver} onPlayAgain={resetGame} />
            <GameWonModal isVisible={showGameWon} onPlayAgain={resetGame} />

            <div style={{ width: "100%", maxWidth: "1200px", margin: "0 auto" }}>
                {/* Lives Display */}
                <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px"
                    }}>

                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "24px",
                                        filter: lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                            : "grayscale(100%) opacity(0.3)",
                                        transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                        transition: "all 0.3s ease"
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>

                    </div>
                </div>

                {/* Description */}

                {!gameStarted && (
                    <div style={{
                        borderRadius: "20px",
                        maxWidth: "770px",
                        background: "linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(29, 78, 216, 0.4))",
                        width: "90%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "15px 20px",
                        margin: "0 auto 30px",
                        color: "white",
                        textAlign: "center",
                        fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
                        lineHeight: "1.6"
                    }}>
                        {selLanguage ? "Σε αυτό το παιχνίδι, έχεις λίγα δευτερόλεπτα για να ρίξεις μια ματιά στα στοιχεία μιας διπλά συνδεδεμένης λίστας. Κάθε στοιχείο δείχνει τη θέση στην οποία ανήκει. Απομνημόνευσέ τα! Αν κάνεις λάθος, θα σου δοθούν λίγα δευτερόλεπτα ακόμα για να τα ξαναδείς." : "In this game, you get a quick look at the elements of a doubly linked list. Each element relates to the spot where it belongs. Memorize their positions! If you mess up, you'll get a few extra seconds to peek at them again."}
                    </div>)}

                {/* Start Button */}
                {!gameStarted && (
                    <div style={{ textAlign: "center", marginBottom: "30px" }}>
                        <button
                            onClick={startGame}
                            style={{
                                background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "16px",
                                padding: "16px 40px",
                                fontSize: "1.2rem",
                                fontWeight: "700",
                                cursor: "pointer",
                                boxShadow: "0 10px 25px rgba(16, 47, 185, 0.4)",
                                transition: "all 0.3s ease",
                                transform: "scale(1)"
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.transform = "scale(1.05)";
                                e.target.style.boxShadow = "0 15px 35px rgba(16, 47, 185, 0.5)";
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = "scale(1)";
                                e.target.style.boxShadow = "0 10px 25px rgba(16, 47, 185, 0.4)";
                            }}
                        >
                            {selLanguage ? "Έναρξη Παιχνιδιού" : "Start Game"}
                        </button>
                    </div>
                )}

                {/* Timer Display */}
                {gameStarted && isRevealed && (
                    <div style={{
                        textAlign: "center",
                        marginBottom: "20px"
                    }}>
                        <div style={{
                            display: "inline-block",
                            padding: "10px 20px",
                            borderRadius: "12px",
                            background: timeLeft <= 3
                                ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                                : "linear-gradient(135deg, #1b0bf5ff 0%, #1406d9ff 100%)",
                            color: "white",
                            fontSize: "1.5rem",
                            fontWeight: "700",
                            border: timeLeft <= 3 ? "2px solid #ef4444" : "2px solid #fffdfaff",
                            boxShadow: timeLeft <= 3
                                ? "0 4px 12px rgba(239, 68, 68, 0.3)"
                                : "0 4px 12px rgba(23, 11, 245, 0.2)",
                            animation: timeLeft <= 3 ? "pulse 0.5s infinite" : "none"
                        }}>
                            {timeLeft}
                        </div>
                    </div>
                )}

                {/* Πλαίσιο με τις κενές θέσεις της λίστας */}
                {gameStarted && (
                    <div style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "100%",
                        border: "3px solid #3b82f6",
                        borderRadius: "20px",
                        margin: "0 auto 30px",
                        background: "#ffffffde",
                        boxShadow: "inset 0 2px 10px rgba(0, 0, 0, 0.1), 0 10px 25px rgba(59, 130, 246, 0.15)",
                        padding: "30px 15px",
                        minHeight: "180px"
                    }}>
                        <div style={{
                            textAlign: "center",
                            fontSize: "1.1rem",
                            fontWeight: "700",
                            color: "#3b82f6",
                            marginBottom: "20px"
                        }}>
                            {selLanguage ? "Τοποθέτησε τους κόμβους στη σωστή θέση!" : "Place the nodes in the correct position!"}
                        </div>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            flexWrap: "nowrap",
                            padding: "10px"
                        }}>
                            {placedNodes.map((nodeId, index) => {
                                const nodeData = nodeId ? getNodeData(nodeId) : null;
                                const isWrong = wrongPlacement === index;

                                return (
                                    <React.Fragment key={index}>
                                        <div
                                            style={{
                                                width: "clamp(80px, 15vw, 110px)",
                                                height: "clamp(100px, 18vw, 130px)",
                                                cursor: "pointer",
                                                transition: "all 0.3s ease",
                                                flexShrink: 0,
                                                ...(nodeId ? {
                                                    borderRadius: "16px",
                                                    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.15)",
                                                    background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                                    color: "white",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                } : {
                                                    border: "3px dashed #94a3b8",
                                                    borderRadius: "16px",
                                                    background: "rgba(148, 163, 184, 0.1)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                }),
                                                ...(isWrong ? {
                                                    animation: "shake 0.5s ease"
                                                } : {})
                                            }}
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDrop(e, index)}
                                        >
                                            {nodeId ? (
                                                <div style={{
                                                    fontSize: "clamp(1rem, 2.5vw, 1.3rem)",
                                                    fontWeight: "700",
                                                    textAlign: "center",
                                                    textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                                                    padding: "12px"
                                                }}>
                                                    {nodeData.id}
                                                </div>
                                            ) : (
                                                <div style={{
                                                    fontSize: "clamp(1.5rem, 3vw, 2rem)",
                                                    fontWeight: "700",
                                                    color: "#94a3b8",
                                                    opacity: "0.5"
                                                }}>
                                                    {index + 1}
                                                </div>
                                            )}
                                        </div>

                                        {index < placedNodes.length - 1 && (
                                            <div style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0
                                            }}>
                                                <div style={{
                                                    fontSize: "clamp(1.2rem, 2.5vw, 1.8rem)",
                                                    color: "#3b82f6",
                                                    fontWeight: "700"
                                                }}>
                                                    ⇄
                                                </div>
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                )}

                {showError && (
                    <div style={{
                        background: "rgba(255, 0, 0, 0.1)",
                        color: "red",
                        padding: "12px",
                        borderRadius: "8px",
                        marginBottom: "12px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontWeight: "600",
                    }}>
                        {errorMessage}
                    </div>
                )}
                {gameStarted && (
                    <div style={{
                        width: "100%",
                        maxWidth: "100%",
                        borderRadius: "20px",
                        margin: "0 auto 30px",
                        background: "#ffffffde",
                        boxShadow: "inset 0 2px 10px rgba(0, 0, 0, 0.1), 0 10px 25px rgba(16, 185, 129, 0.15)",
                        padding: "30px 20px",
                        minHeight: "140px"
                    }}>
                        <div style={{
                            textAlign: "center",
                            fontSize: "1.1rem",
                            fontWeight: "700",
                            color: "#2f303eff",
                            marginBottom: "20px"
                        }}>
                            {isRevealed ? selLanguage ? "Κάρτες που πρέπει να θυμάσαι" : "Cards to remember" :
                                selLanguage ? "Σύρε τις κάρτες στη σωστή θέση" : "Drag the cards to the correct position"}
                        </div>
                        <div style={{
                            display: "flex",
                            gap: "clamp(8px, 1.5vw, 15px)",
                            justifyContent: "center",
                            alignItems: "center",
                            flexWrap: "nowrap",
                            padding: "0 10px"
                        }}>
                            {availableNodes.map((nodeId) => {
                                const nodeData = getNodeData(nodeId);
                                return (
                                    <div
                                        key={nodeId}
                                        draggable={hasStarted && !isRevealed}
                                        onDragStart={hasStarted && !isRevealed ? (e) => handleDragStart(e, nodeId) : undefined}
                                        style={{
                                            width: `calc((100% - ${availableNodes.length - 1} * clamp(8px, 1.5vw, 15px)) / ${availableNodes.length})`,
                                            maxWidth: "120px",
                                            minWidth: "70px",
                                            aspectRatio: "0.85",
                                            borderRadius: "16px",
                                            boxShadow: "0 8px 20px rgba(16, 185, 129, 0.3)",
                                            display: "flex",
                                            flexDirection: "column",
                                            padding: "12px",
                                            background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                            color: "white",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "clamp(0.9rem, 2.5vw, 1.3rem)",
                                            fontWeight: "700",
                                            textAlign: "center",
                                            textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                                            transition: "all 0.3s ease",
                                            cursor: hasStarted && !isRevealed ? "grab" : "default",
                                            userSelect: "none",
                                            flexShrink: 0
                                        }}
                                        onMouseEnter={(e) => {
                                            if (hasStarted && !isRevealed) {
                                                e.currentTarget.style.transform = "scale(1.05)";
                                                e.currentTarget.style.boxShadow = "0 12px 30px rgba(16, 185, 129, 0.4)";
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (hasStarted && !isRevealed) {
                                                e.currentTarget.style.transform = "scale(1)";
                                                e.currentTarget.style.boxShadow = "0 8px 20px rgba(16, 185, 129, 0.3)";
                                            }
                                        }}
                                    >
                                        {isRevealed ? nodeData.id : "?"}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

            </div>

            <style>{`
                    @keyframes shake {
                        0%, 100% { transform: translateX(0); }
                        25% { transform: translateX(-10px); }
                        75% { transform: translateX(10px); }
                    }
                    @keyframes pulse {
                        0%, 100% { transform: scale(1); }
                        51% { transform: scale(1.05); }
                    }
                `}</style>
        </div>
    );
};

export default DoublyLinkedListIntro;